import { useParams, Link, useLocation } from 'react-router-dom'
import { ArrowLeft, Target, Users, Layers, Building2, TrendingUp, BookOpen, Handshake } from 'lucide-react'
import { pianiDiZona, type SchedaObiettivo, type GruppoPianoZona, type ClusterTarget, type ObiettivoCluster } from '../../data/pianiDiZona'
import BSDCard from '../BSDCard'

const CLUSTER_COLORS: Record<ClusterTarget, string> = {
  Adulti: '#3b82f6',
  Minori: '#10b981',
  Anziani: '#f59e0b',
}

const OBIETTIVO_CLUSTER_COLORS: Record<ObiettivoCluster, string> = {
  'Inclusione': '#0ea5e9',
  'Benessere sociale': '#7c3aed',
  'Sistema': '#10b981',
  'Interambito': '#f59e0b',
}

function pdzSchedaSlug(titolo: string): string {
  return titolo
    .toLowerCase()
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

export { pdzSchedaSlug }

function matchesSchedaSlug(scheda: SchedaObiettivo, slug: string): boolean {
  return pdzSchedaSlug(scheda.titolo) === slug || pdzSchedaSlug(scheda.obiettivo) === slug
}

function findSchedaInPiano(ambitoKey: string, slug: string): { scheda: SchedaObiettivo; gruppo: GruppoPianoZona; ambitoKey: string } | undefined {
  const piano = pianiDiZona[ambitoKey]
  if (!piano) return undefined

  for (const gruppo of piano.gruppi) {
    const scheda = gruppo.schede.find(s => matchesSchedaSlug(s, slug))
    if (scheda) return { scheda, gruppo, ambitoKey }
  }

  return undefined
}

function findSchedaBySlug(ambito: string, slug: string): { scheda: SchedaObiettivo; gruppo: GruppoPianoZona; ambitoKey: string } | undefined {
  const ambitoKey = Object.keys(pianiDiZona).find(k => k.toLowerCase() === ambito.toLowerCase())

  if (ambitoKey) {
    const exactMatch = findSchedaInPiano(ambitoKey, slug)
    if (exactMatch) return exactMatch
  }

  const fallbackMatches = Object.keys(pianiDiZona)
    .map(key => findSchedaInPiano(key, slug))
    .filter((match): match is { scheda: SchedaObiettivo; gruppo: GruppoPianoZona; ambitoKey: string } => Boolean(match))

  if (fallbackMatches.length === 1) return fallbackMatches[0]
  return fallbackMatches[0]
}

export default function PdzDetailPage() {
  const { ambito, slug } = useParams<{ ambito: string; slug: string }>()
  const location = useLocation()
  const state = location.state as { from?: string } | null
  const backTo = state?.from === '/pianidizona' ? '/pianidizona' : '/'
  const result = ambito && slug ? findSchedaBySlug(ambito, slug) : undefined

  if (!result) {
    return (
      <div className="cdc-detail-page">
        <div className="cdc-detail-container">
          <Link to={backTo} className="cdc-detail-back"><ArrowLeft size={16} /> Torna</Link>
          <h1>Obiettivo non trovato</h1>
        </div>
      </div>
    )
  }

  const { scheda, gruppo, ambitoKey } = result
  const colore = gruppo.colore
  const obiettivoClusterColor = OBIETTIVO_CLUSTER_COLORS[scheda.obiettivo_cluster] ?? colore
  const hasIndicatori = (scheda.indicatoriOutput?.length ?? 0) > 0 || (scheda.indicatoriOutcome?.length ?? 0) > 0
  const hasPartners = scheda.partners && scheda.partners.length > 0

  return (
    <div className="cdc-detail-page">
      <div className="cdc-detail-container">
        <Link to={backTo} className="cdc-detail-back"><ArrowLeft size={16} /> Torna</Link>

        {/* ── Header ── */}
        <div className="cdc-detail-header">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999,
              color: colore, background: `${colore}18`, border: `1px solid ${colore}44`,
            }}>
              {gruppo.titolo}
            </span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999,
              color: 'var(--accent-blue)', background: 'hsla(210,100%,56%,0.08)', border: '1px solid hsla(210,100%,56%,0.2)',
            }}>
              <Layers size={12} /> Ambito di {ambitoKey}
            </span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 999,
              color: obiettivoClusterColor, background: `${obiettivoClusterColor}15`, border: `1px solid ${obiettivoClusterColor}40`,
            }}>
              {scheda.obiettivo_cluster}
            </span>
          </div>

          <h1 className="cdc-detail-title">{scheda.titolo}</h1>
          {scheda.sottotitolo && (
            <p style={{ color: 'var(--text-2)', fontSize: 13, fontStyle: 'italic', margin: '4px 0 10px' }}>— {scheda.sottotitolo}</p>
          )}

          <div className="cdc-detail-meta" style={{ marginTop: 10 }}>
            {scheda.target_cluster.map(c => (
              <span key={c} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
                color: CLUSTER_COLORS[c], background: `${CLUSTER_COLORS[c]}15`, border: `1px solid ${CLUSTER_COLORS[c]}40`,
              }}>
                <Users size={11} /> {c}
              </span>
            ))}
            {scheda.focus_disabilita && (
              <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999, color: '#d97706', background: '#fef3c720', border: '1px solid #d9770640' }}>
                Focus Disabilità
              </span>
            )}
            {scheda.coinvolgimentoASST && (
              <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999, color: colore, background: `${colore}15`, border: `1px solid ${colore}40` }}>
                Coinvolgimento ASST
              </span>
            )}
            {scheda.nuovoServizio && (
              <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999, color: 'var(--text-2)', background: 'var(--bg-3)', border: '1px solid var(--border)' }}>
                Nuovo servizio
              </span>
            )}
            {scheda.continuita2021 && (
              <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999, color: 'var(--text-2)', background: 'var(--bg-3)', border: '1px solid var(--border)' }}>
                Continuità 2021
              </span>
            )}
          </div>
        </div>

        {/* ── Info row ── */}
        <div className="cdc-detail-row">
          <BSDCard kind="custom" className="cdc-detail-info-card" sezione="Piano di Zona">
            <h2 className="cdc-detail-section-title"><Target size={16} /> Obiettivo e target</h2>
            <dl className="cdc-detail-dl">
              <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 10, fontSize: 13, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <dt style={{ color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.03em', paddingTop: 2 }}>Obiettivo</dt>
                <dd style={{ color: 'var(--text-1)', lineHeight: 1.65, margin: 0 }}>{scheda.obiettivo}</dd>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 10, fontSize: 13, padding: '8px 0', borderBottom: scheda.cooperazioneAltriAmbiti ? '1px solid var(--border)' : 'none' }}>
                <dt style={{ color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.03em', paddingTop: 2 }}>Target</dt>
                <dd style={{ color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>{scheda.target}</dd>
              </div>
              {scheda.cooperazioneAltriAmbiti && (
                <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 10, fontSize: 13, padding: '8px 0' }}>
                  <dt style={{ color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.03em', paddingTop: 2 }}>Cooperazione</dt>
                  <dd style={{ color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>{scheda.cooperazioneAltriAmbiti}</dd>
                </div>
              )}
            </dl>
          </BSDCard>

          <BSDCard kind="custom" className="cdc-detail-info-card" sezione="Piano di Zona">
            <h2 className="cdc-detail-section-title"><BookOpen size={16} /> Scheda</h2>
            <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
              <div style={{ textAlign: 'center', flex: 1, background: 'var(--bg-2)', borderRadius: 10, padding: '12px 8px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 28, fontWeight: 800, fontFamily: 'var(--font-mono)', color: colore, lineHeight: 1 }}>{scheda.azioni.length}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Azioni</div>
              </div>
              <div style={{ textAlign: 'center', flex: 1, background: 'var(--bg-2)', borderRadius: 10, padding: '12px 8px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 28, fontWeight: 800, fontFamily: 'var(--font-mono)', color: colore, lineHeight: 1 }}>{scheda.risorse.length}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Risorse</div>
              </div>
              {hasPartners && (
                <div style={{ textAlign: 'center', flex: 1, background: 'var(--bg-2)', borderRadius: 10, padding: '12px 8px', border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, fontFamily: 'var(--font-mono)', color: colore, lineHeight: 1 }}>{scheda.partners!.length}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Partner</div>
                </div>
              )}
            </div>
            {scheda.areePolicy && scheda.areePolicy.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Aree di Policy</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 16 }}>
                  {scheda.areePolicy.map((area, idx) => (
                    <span key={idx} style={{ fontSize: 12, color: 'var(--text-2)', background: 'var(--bg-3)', padding: '3px 10px', borderRadius: 6, border: '1px solid var(--border)' }}>
                      {area}
                    </span>
                  ))}
                </div>
              </>
            )}
            {scheda.bisogniPdf && scheda.bisogniPdf.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Bisogni</div>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {scheda.bisogniPdf.map((b, idx) => (
                    <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, color: 'var(--text-2)', fontSize: 13, lineHeight: 1.5 }}>
                      <span style={{ color: colore, flexShrink: 0, marginTop: 2, fontSize: 10 }}>●</span>{b}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </BSDCard>
        </div>

        {/* ── Azioni ── */}
        <BSDCard kind="custom" className="cdc-detail-services-card" sezione="Piano di Zona">
          <h2 className="cdc-detail-section-title">
            <Building2 size={16} /> Azioni Programmate
            <span className="cdc-detail-services-count">{scheda.azioni.length}</span>
          </h2>
          <div className="cdc-detail-sections">
            <div className="cdc-detail-sezione" style={{ borderLeft: `3px solid ${colore}` }}>
              <div className="cdc-detail-servizi-grid">
                {scheda.azioni.map((azione, idx) => (
                  <div key={idx} className="cdc-detail-servizio">
                    <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13 }}>
                      <span style={{ fontWeight: 700, color: 'var(--text-0)', marginRight: 6, fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      {azione}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </BSDCard>

        {/* ── Risorse + Partner ── */}
        <div className="cdc-detail-row" style={{ gridTemplateColumns: hasPartners ? '1fr 1fr' : '1fr' }}>
          <BSDCard kind="custom" className="cdc-detail-info-card" sezione="Piano di Zona">
            <h2 className="cdc-detail-section-title"><TrendingUp size={16} /> Risorse</h2>
            <div className="cdc-detail-sections">
              <div className="cdc-detail-sezione">
                <div className="cdc-detail-servizi-grid">
                  {scheda.risorse.map((r, idx) => (
                    <div key={idx} className="cdc-detail-servizio">
                      <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13 }}>{r}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </BSDCard>
          {hasPartners && (
            <BSDCard kind="custom" className="cdc-detail-info-card" sezione="Piano di Zona">
              <h2 className="cdc-detail-section-title"><Handshake size={16} /> Partner</h2>
              <div className="cdc-detail-sections">
                <div className="cdc-detail-sezione">
                  <div className="cdc-detail-servizi-grid">
                    {scheda.partners!.map((p, idx) => (
                      <div key={idx} className="cdc-detail-servizio">
                        <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13 }}>{p}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </BSDCard>
          )}
        </div>

        {/* ── Indicatori ── */}
        {hasIndicatori && (
          <BSDCard kind="custom" className="cdc-detail-services-card" sezione="Piano di Zona">
            <h2 className="cdc-detail-section-title"><TrendingUp size={16} /> Indicatori</h2>
            <div className="cdc-detail-sections">
              {scheda.indicatoriOutput && scheda.indicatoriOutput.length > 0 && (
                <div className="cdc-detail-sezione">
                  <h3 className="cdc-detail-sezione-title">Output</h3>
                  <div className="cdc-detail-servizi-grid">
                    {scheda.indicatoriOutput.map((ind, idx) => (
                      <div key={idx} className="cdc-detail-servizio">
                        <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13 }}>
                          <span style={{ color: colore, marginRight: 6 }}>→</span>{ind}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {scheda.indicatoriOutcome && scheda.indicatoriOutcome.length > 0 && (
                <div className="cdc-detail-sezione">
                  <h3 className="cdc-detail-sezione-title">Outcome</h3>
                  <div className="cdc-detail-servizi-grid">
                    {scheda.indicatoriOutcome.map((ind, idx) => (
                      <div key={idx} className="cdc-detail-servizio">
                        <p className="cdc-detail-servizio-desc" style={{ margin: 0, fontSize: 13 }}>
                          <span style={{ color: colore, marginRight: 6 }}>→</span>{ind}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </BSDCard>
        )}

      </div>
    </div>
  )
}
